import { useEffect, useRef, useState, MutableRefObject } from "react";
import { getRenderingEngine } from "@cornerstonejs/core";
import { initCornerstone } from "../utils/cornerstone/initCornerstone";
import { setupEventListeners } from "../utils/cornerstone/eventHandlers";
import { updateClippingPlane } from "../utils/vtk/clippingPlane";
import { manualZoom, updateWindowLevel } from "../utils/viewport/volumeUtils";
import { createResizeHandler } from "../utils/cornerstone/resizeHandler";

interface UseCornerstoneProps {
  element1Ref: React.RefObject<HTMLDivElement>;
  element2Ref: React.RefObject<HTMLDivElement>;
  element3Ref: React.RefObject<HTMLDivElement>;
  onWorldPosChange?: (pos: number[] | null) => void;
  caseId?: string | number;
  editorMode?: boolean;
}

export const useCornerstone = ({
  element1Ref,
  element2Ref,
  element3Ref,
  onWorldPosChange,
  caseId,
  editorMode = false,
}: UseCornerstoneProps) => {
  const running = useRef(false);
  const sphereActorRef = useRef<any>(null);
  const clippingPlaneRef = useRef<any>(null);
  const volumeActorRef = useRef<any>(null);
  const segmentationManagerRef = useRef<any>(null);
  const toolGroupRef = useRef<any>(null);
  const [dataSource, setDataSource] = useState<string>("Loading...");
  const [clipPlanePosition, setClipPlanePosition] = useState<number>(0.5);
  const [windowWidth, setWindowWidth] = useState<number>(400);
  const [windowLevel, setWindowLevel] = useState<number>(40);

  const viewportids = {
    element1Ref: "CT_AXIAL",
    element2Ref: "CT_SAGITTAL",
    element3Ref: "CT_CORONAL",
  };

  // Initialize Cornerstone
  useEffect(() => {
    let isMounted = true;
    let cleanupFn: (() => void) | undefined;

    // Create a short delay to ensure the DOM elements are fully rendered
    const setupTimeout = setTimeout(() => {
      if (
        !element1Ref.current ||
        !element2Ref.current ||
        !element3Ref.current
      ) {
        console.warn("DOM elements not ready yet, will retry...");
        return;
      }

      if (!isMounted) return;

      const setup = async () => {
        if (running.current) {
          return;
        }

        // Check if rendering engine already exists and clean it up first
        try {
          const existingEngine = getRenderingEngine("myRenderingEngine");
          if (existingEngine) {
            console.log("Found existing rendering engine, cleaning up...");
            existingEngine.destroy();
            // Small delay to ensure cleanup completes
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (error) {
          // Engine doesn't exist, which is fine
        }

        running.current = true;

        try {
          // Initialize Cornerstone and set up viewports
          const {
            volumeActorRef: volActor,
            clippingPlaneRef: clipPlane,
            segmentationManager,
            toolGroup,
            cleanup: cornerstoneCleanup,
          } = await initCornerstone({
            element1Ref,
            element2Ref,
            element3Ref,
            setDataSource,
            caseId,
            editorMode,
          });

          // Store the segmentation manager and tool group references
          segmentationManagerRef.current = segmentationManager;
          toolGroupRef.current = toolGroup;

          // Get the actual window/level values from Cornerstone after initialization
          const renderingEngine = getRenderingEngine("myRenderingEngine");
          if (renderingEngine) {
            const viewport = renderingEngine.getViewport("CT_AXIAL");
            if (viewport) {
              const voiRange = viewport.getProperties()?.voiRange;
              if (voiRange) {
                const actualWindowWidth = voiRange.upper - voiRange.lower;
                const actualWindowLevel = (voiRange.upper + voiRange.lower) / 2;

                console.log("=== CORNERSTONE INITIALIZATION ===");
                console.log("Initial WW/WL from React state:", {
                  windowWidth,
                  windowLevel,
                });
                console.log("Actual WW/WL from Cornerstone:", {
                  windowWidth: actualWindowWidth,
                  windowLevel: actualWindowLevel,
                });
                console.log("VOI Range:", voiRange);

                // Update React state to match Cornerstone's actual values
                setWindowWidth(actualWindowWidth);
                setWindowLevel(actualWindowLevel);
              }
            }
          }

          const refs = [element1Ref, element2Ref, element3Ref];
          const listeners: Array<() => void> = [];

          refs.forEach((ref) => {
            const element = ref.current;
            if (!element) return;

            // Handler for mousemove
            const handler = (evt: MouseEvent) => {
              const rect = element.getBoundingClientRect();
              const canvasPos: [number, number] = [
                Math.floor(evt.clientX - rect.left),
                Math.floor(evt.clientY - rect.top),
              ];
              const renderingEngine = getRenderingEngine("myRenderingEngine");
              if (!renderingEngine) return;

              const viewport = renderingEngine.getViewport(element.id);
              if (!viewport) return;
              const worldPos = viewport.canvasToWorld(canvasPos);

              // Call the world position change callback if provided
              if (onWorldPosChange) onWorldPosChange(worldPos);

              // Check for proximity to segmentations
              if (segmentationManagerRef.current) {
                segmentationManagerRef.current.checkSegmentationProximity(
                  worldPos,
                  element.id
                );
              }
            };

            element.addEventListener("mousemove", handler);
            // Store cleanup function
            listeners.push(() =>
              element.removeEventListener("mousemove", handler)
            );
          });

          // Store refs for later use
          // volumeActorRef.current = volActor.current;
          // clippingPlaneRef.current = clipPlane.current;

          // Set up event listeners for crosshairs, etc.
          cleanupFn = setupEventListeners(sphereActorRef);

          // Set up initial clipping plane
          // updateClippingPlane(
          //   clippingPlaneRef as MutableRefObject<any>,
          //   volumeActorRef as MutableRefObject<any>,
          //   clipPlanePosition
          // );

          // Set up window resize handler
          const handleResize = createResizeHandler("myRenderingEngine", 200); // 200ms debounce
          console.log("Setting up window resize handler");
          window.addEventListener("resize", handleResize);

          // Store original cleanup function
          const originalCleanupFn = cleanupFn;

          // Create new cleanup function that also removes the resize listener
          cleanupFn = () => {
            console.log("Removing window resize handler");
            window.removeEventListener("resize", handleResize);
            if (originalCleanupFn) originalCleanupFn();
            // Call the proper Cornerstone cleanup
            if (cornerstoneCleanup) cornerstoneCleanup();
          };
        } catch (error) {
          console.error("Error in Cornerstone setup:", error);
          running.current = false;
        }
      };

      setup();
    }, 300); // 300ms delay for DOM rendering

    return () => {
      isMounted = false;
      clearTimeout(setupTimeout);

      // Call cleanup function if it exists
      if (cleanupFn) cleanupFn();

      // Reset the running flag
      running.current = false;
    };
  }, [element1Ref, element2Ref, element3Ref, onWorldPosChange]);

  // Update clipping plane when position changes
  useEffect(() => {
    if (running.current && clippingPlaneRef.current) {
      updateClippingPlane(
        clippingPlaneRef as MutableRefObject<any>,
        volumeActorRef as MutableRefObject<any>,
        clipPlanePosition
      );
    }
  }, [clipPlanePosition]);

  // Handle clipping plane position change
  const handleClipPositionChange = (newPosition: number) => {
    setClipPlanePosition(newPosition);
  };

  // Handle zoom for specific viewport
  const handleZoom = (viewportId: string, direction: "in" | "out") => {
    manualZoom("myRenderingEngine", viewportId, direction);
  };

  // Handle window width change
  const handleWindowWidthChange = (newWidth: number) => {
    setWindowWidth(newWidth);
  };

  // Handle window level change
  const handleWindowLevelChange = (newLevel: number) => {
    setWindowLevel(newLevel);
  };

  // Update window width/level when they change
  useEffect(() => {
    if (running.current) {
      updateWindowLevel("myRenderingEngine", windowWidth, windowLevel);
    }
  }, [windowWidth, windowLevel]);

  // Create function to manually trigger resize (used when container size changes without window resize)
  const handleManualResize = () => {
    if (running.current) {
      console.log("Manual resize triggered");
      const renderingEngine = getRenderingEngine("myRenderingEngine");
      if (renderingEngine) {
        try {
          renderingEngine.resize(true);
          console.log("Manual resize completed");
        } catch (error) {
          console.error("Error during manual resize:", error);
        }
      }
    }
  };

  return {
    dataSource,
    clipPlanePosition,
    windowWidth,
    windowLevel,
    handleClipPositionChange,
    handleWindowWidthChange,
    handleWindowLevelChange,
    handleZoom,
    handleManualResize,
    segmentationManager: segmentationManagerRef.current,
    toolGroup: toolGroupRef.current,
    running: running.current,
  };
};
